import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Crown, Users, Building2, Mail, AlertTriangle, ShieldCheck, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Enterprise Executive Dashboard — multi-team rollup.
 *
 * Reads GET /api/organization/rollup which collects every team sharing
 * the caller's `parent_organization_id` (or just the caller's team when
 * none is set — useful for solo-Enterprise tenants).
 *
 * Renders:
 *  - Global HOLD / CAUTION / READY counters
 *  - One card per team with its own counter pill + top-5 worst-band roster
 *  - A direct link to per-team Department Verdict Board
 *
 * Falls back to a friendly "talk to your success engineer" CTA when the
 * rollup endpoint returns zero teams (shouldn't happen but defensive).
 */
export default function ExecutiveDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/organization/rollup");
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not load organization rollup");
      }
    })();
  }, []);

  if (err) {
    return (
      <div className="space-y-6" data-testid="executive-dashboard-page">
        <ExecutiveHeader />
        <div className="rounded-2xl ink-card p-6 text-sm text-red-300" data-testid="executive-error">{String(err)}</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="space-y-6" data-testid="executive-dashboard-page">
        <ExecutiveHeader />
        <div className="flex items-center gap-2 text-white/55 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Computing rollup…</div>
      </div>
    );
  }

  const t = data.totals || {};
  return (
    <div className="space-y-8" data-testid="executive-dashboard-page">
      <ExecutiveHeader teamCount={data.team_count} />

      {/* Global rollup tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="executive-global-counters">
        <Counter label="Teams" value={data.team_count} accent="#FFD700" testid="exec-counter-teams" />
        <Counter label="HOLD" value={t.holds} accent="#EF4444" testid="exec-counter-holds" Icon={AlertTriangle} />
        <Counter label="CAUTION" value={t.cautions} accent="#F59E0B" testid="exec-counter-cautions" />
        <Counter label="READY" value={t.ready} accent="#10B981" testid="exec-counter-ready" Icon={ShieldCheck} />
      </div>

      <div className="space-y-4">
        {(data.teams || []).map((team) => (
          <TeamCard key={team.team_id} team={team} />
        ))}
        {(data.teams || []).length === 0 && (
          <div className="rounded-2xl ink-card p-6 text-sm text-white/55">No teams in this organization yet.</div>
        )}
      </div>

      <section className="rounded-2xl ink-card p-6 md:p-7" data-testid="executive-callout">
        <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] tracking-[0.2em] font-bold uppercase"
          style={{ backgroundColor: "rgba(255,215,0,0.18)", color: "#FFD700" }}>
          <Building2 className="h-3 w-3" /> Your dedicated success engineer
        </div>
        <h2 className="font-display font-black text-2xl tracking-tighter text-white mt-3">
          Add an affiliate or schedule your quarterly review.
        </h2>
        <p className="text-sm text-white/65 mt-2 max-w-xl">
          To onboard a new team into this organization, your Success Engineer will set the team&apos;s <code className="text-white">parent_organization_id</code> to your org. Reach out below.
        </p>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <Link to="/enterprise/contact">
            <Button className="text-[var(--ink-0)] font-bold rounded-full" style={{ backgroundColor: "#FFD700" }} data-testid="executive-contact">
              <Mail className="h-4 w-4 mr-2" /> Email Success Engineer
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

function ExecutiveHeader({ teamCount }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] font-bold uppercase" style={{ color: "#FFD700" }}>
        <Crown className="h-3.5 w-3.5" /> Enterprise · Executive View
        {typeof teamCount === "number" && (
          <span className="text-white/45 ml-2 font-mono-num">{teamCount} team{teamCount === 1 ? "" : "s"} in rollup</span>
        )}
      </div>
      <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-white leading-[1.05] mt-1">
        Your organization, end-to-end.
      </h1>
      <p className="text-white/65 mt-3 leading-relaxed max-w-2xl">
        Roll-up across every team in your tenant — readiness verdicts, cross-module cause rules, workload trends.
      </p>
    </div>
  );
}

function Counter({ label, value, accent, testid, Icon }) {
  return (
    <div className="rounded-xl ink-card p-4" data-testid={testid}>
      <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-white/45 flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className="font-display font-black text-4xl font-mono-num mt-1" style={{ color: accent }}>{value ?? 0}</div>
    </div>
  );
}

function TeamCard({ team }) {
  const t = team.totals || {};
  const top = team.top_rows || [];
  return (
    <div className="rounded-2xl ink-card p-5 md:p-6" data-testid={`executive-team-card-${team.team_id}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/45">{team.tier.toUpperCase()} tier</div>
          <h3 className="font-display font-black text-xl text-white mt-0.5">{team.name}</h3>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <CountPill label="HOLD" value={t.holds} accent="#EF4444" />
          <CountPill label="CAUTION" value={t.cautions} accent="#F59E0B" />
          <CountPill label="READY" value={t.ready} accent="#10B981" />
          <span className="text-white/45 ml-2">· {t.total} athletes</span>
        </div>
      </div>
      {top.length > 0 && (
        <ul className="mt-3 divide-y divide-white/5">
          {top.map((row) => {
            const band = row.overall_band;
            const color = band === "hold" ? "#EF4444" : band === "caution" ? "#F59E0B" : "#10B981";
            return (
              <li key={row.player_user_id} className="py-2 flex items-center justify-between text-sm">
                <span className="text-white/85 truncate">{row.player_name || "—"}</span>
                <span className="font-mono-num text-xs font-bold uppercase tracking-[0.15em]" style={{ color }}>
                  {band} · {Math.round(row.overall_score)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <div className="mt-4 text-right">
        <Link to="/dashboard/roster" className="text-xs tracking-[0.15em] uppercase font-bold text-white/55 hover:text-white inline-flex items-center gap-1">
          Open team roster <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}

function CountPill({ label, value, accent }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded font-bold uppercase tracking-[0.15em] text-[10px]"
      style={{ backgroundColor: `${accent}20`, color: accent, border: `1px solid ${accent}55` }}>
      {label} <span className="font-mono-num">{value ?? 0}</span>
    </span>
  );
}
